import React, { useEffect, useLayoutEffect, useCallback, useRef, useState } from 'react';
import '../Skeletons/skeleton-animations.css';



const animationsMap = {
  pulse: 'skeleton-pulse',
  shimmer: 'skeleton-basic-shimmer',
  syncWave: 'sync-wave',
  metalicCircle: 'skeleton-circle',
  metalicCircleBordered: 'skeleton-circle skeleton-circle--bordered',
  metalicCircleCompact: 'skeleton-circle  skeleton-circle--compact',
  coinGlint: 'skeleton-sunlight-coin',
}


interface SkeletonizeProps {
  children: React.ReactNode | ((args: { disableSkeleton: boolean; isLoading: boolean }) => React.ReactNode);
  isLoading: boolean;
  disableSkeleton?: boolean;
  width?: string | number;
  height?: string | number;
  rounded?: string;
  backgroundColor?: string;
  className?: string;
  animation?: string;
  skeletonCount?: number; // Number of skeleton items to show when children is empty
  componentFactory?: (key: React.Key, isLoading: boolean) => React.ReactNode; // Factory function to create actual components instead of divs
  fadeOutAnimation?: string; // CSS class name for fade-out animation
  fadeOutDuration?: number; // Duration in milliseconds for fade-out transition
}

const Skeletonize: React.FC<SkeletonizeProps> = (props) => {
  const {
    children,
    isLoading,
    disableSkeleton = false,
    width,
    height,
    rounded,
    backgroundColor,
    className,
    animation,
    skeletonCount,
    componentFactory,
    fadeOutAnimation = 'skeleton-fade-out',
    fadeOutDuration = 700,
  } = props;


  const [skeletonToElementTransition, setskeletonToElementTransition] = useState(false);
  

  // Handle fade sequence when isLoading changes from true to false
  useLayoutEffect(() => {
    if (skeletonToElementTransition) {
      
      // After fade completes, hide skeleton and show content
      const fadeTimeout = setTimeout(() => {
        setskeletonToElementTransition(false);
      }, fadeOutDuration); // Use configurable fade-out duration
      
      return () => clearTimeout(fadeTimeout);
    }
  }, [isLoading, skeletonToElementTransition, fadeOutDuration]);

  // Reset states when loading starts again
  useLayoutEffect(() => {
    setskeletonToElementTransition(!isLoading);
  }, [isLoading]);


  if (disableSkeleton) {
    if (typeof children === 'function') {
      return <>{children({ disableSkeleton: true, isLoading })}</>;
    }
    return <React.Fragment>{children}</React.Fragment>;
  }


  // If loading and no children (or empty array), create placeholder skeletons
  if ((isLoading || skeletonToElementTransition) && (!children || (Array.isArray(children) && children.length === 0))) {
    const count = skeletonCount ?? 0;
    
    // If count is 0, just render empty
    if (count === 0) {
      return <></>;
    }
    
    // If componentFactory is provided, use it to create actual components
    if (componentFactory) {
      return (
        <>
          {Array.from({ length: count }, (_, index) => 
            componentFactory(`skeleton-${index}`, isLoading)
          )}
        </>
      );
    }
    
    // Create generic skeleton divs (fallback)
    const placeholderChildren = Array.from({ length: count }, (_, index) => (
      <div key={`skeleton-${index}`} />
    ));
  
    return (
      <>
        {placeholderChildren.map((child, index) => (
          <SkeletonWrapper 
            key={`skeleton-${index}`}
            isLoading={true} 
            skeletonToElementTransition={skeletonToElementTransition}
            width={width}
            height={height}
            rounded={rounded}
            backgroundColor={backgroundColor}
            className={className}
            animation={animation}
            fadeOutAnimation={fadeOutAnimation}
            fadeOutDuration={fadeOutDuration}
          >
            {child}
          </SkeletonWrapper>
        ))}
      </>
    );
  }
  
  // Handle children function pattern (like DoctorsListSkeleton)
  if (typeof children === 'function') {
    if (isLoading || skeletonToElementTransition) {
      const count = skeletonCount ?? 0;
      
      if (count === 0) {
        return <></>;
      }
      
      // If componentFactory is provided, use it for skeleton components
      if (componentFactory) {
        return (
          <>
            {Array.from({ length: count }, (_, index) => 
              componentFactory(`skeleton-${index}`, isLoading)
            )}
          </>
        );
      }
    }
    
    // Render actual content when not loading and transition is complete
    if (!isLoading && !skeletonToElementTransition) {
      return <>{children({ disableSkeleton: true, isLoading })}</>;
    }
    
    return <></>;
  }

  return (
    <SkeletonWrapper 
      isLoading={isLoading} 
      skeletonToElementTransition={skeletonToElementTransition}
      width={width}
      height={height}
      rounded={rounded}
      backgroundColor={backgroundColor}
      className={className}
      animation={animation}
      fadeOutAnimation={fadeOutAnimation}
      fadeOutDuration={fadeOutDuration}
    >
      {children}
    </SkeletonWrapper>
  );
};

export default Skeletonize;


type SkeletonWrapperProps = {
  children: React.ReactNode;
  isLoading: boolean;
  skeletonToElementTransition: boolean;
  width?: string | number;
  height?: string | number;
  rounded?: string;
  backgroundColor?: string;
  className?: string;
  animation?: string;
  fadeOutAnimation?: string;
  fadeOutDuration: number;
}


const SkeletonWrapper: React.FC<SkeletonWrapperProps> = ({
  children,
  isLoading,
  skeletonToElementTransition,
  width,
  height,
  rounded = '[8px]',
  className = '',
  animation = 'pulse',
  backgroundColor = '#d1d5dbCC',
  fadeOutAnimation = 'skeleton-fade-out',
  fadeOutDuration
}) => {
  const tickingRef = useRef(false);
  
  const updateOffsets = useCallback(() => {
    const els = Array.from(document.querySelectorAll('.sync-wave'));
    if (!els.length) return;

    const vw = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
    const inv = 100 / vw; // px → vw conversion factor
    
    for (const el of els) {
      const rect = el.getBoundingClientRect();
      // rect.left is relative to the *viewport*; that's what we want
      const leftVW = rect.left * inv;      // value in vw
      (el as HTMLElement).style.setProperty('--wave-offset', leftVW + 'vw');
    }
    tickingRef.current = false;
  }, []);

  const schedule = useCallback(() => {
    if (!tickingRef.current) {
      tickingRef.current = true;
      requestAnimationFrame(updateOffsets);
    }
  }, [updateOffsets]);


  useEffect(() => {
    const els = Array.from(document.querySelectorAll('.sync-wave'));
    if (!els.length) return;

    // Initial update
    updateOffsets();

    // Event listeners
    const handleLoad = () => updateOffsets();
    const handleResize = () => schedule();
    const handleScroll = () => schedule();

    window.addEventListener('load', handleLoad, { once: true });
    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });

    // ResizeObserver for layout changes
    const resizeObserver = new ResizeObserver(schedule);
    els.forEach(el => resizeObserver.observe(el));

    // Cleanup function
    return () => {
      window.removeEventListener('load', handleLoad);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [updateOffsets, schedule]);

  // Show actual content when not loading and fade is complete
  if (!isLoading && !skeletonToElementTransition) {
    return <>{children}</>;
  }


    // // Show actual content when not loading and fade is complete
    // if (!isLoading) {
    //   return <>{children}</>;
    // }

  const animationList = animation.split(' ') as (keyof typeof animationsMap)[];
  const animationClass = animationList.map(animation => animationsMap[animation]).join(' ');
  
  const overlayStyles: React.CSSProperties = {
    ...(width && { width: typeof width === 'number' ? `${width}px` : width }),
    ...(height && { height: typeof height === 'number' ? `${height}px` : height }),
    backgroundColor: backgroundColor,
    '--fade-out-duration': `${fadeOutDuration}ms`
  } as React.CSSProperties;

  return (
    // skeleton wrapper
    <div 
      className={`
        relative 
        rounded-${rounded}
        ${animationClass}
        ${className} 
        ${skeletonToElementTransition ? fadeOutAnimation : ''}
      `}
      style={overlayStyles}
    >
      
      {/* Original content - hidden but keeps layout */}
      <div className="invisible">
        {children}
      </div>
      
      {/* Skeleton overlay - apply default styles if no className is provided*/}
      <div 
        className={`
          absolute inset-0
        `}
      />
    </div>
  );
}
